import {Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import * as _ from "lodash";
import { ToastrService } from "ngx-toastr";
import { PrintDocument } from "../../../models/print-document";
import { FormioControllerService } from "../../../services/form-controller/formio-controller.service";
import { PrintDocumentControllerService } from "../../../services/print-document/print-document-controller.service";
import { Tag } from "../../../models/tag";
import { TagControllerService } from "../../../services/form-controller/tag-controller.service";
import {TranslatedBaseError} from "../TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";
import { environment } from "../../../../environments/environment";
import FormUtils from "../../utils/form-utils.component";
import { Store } from "../../../services/store.service";
import {TutorialBase} from "../../../models/tutorial-base";
import {DeterminantControllerService} from "../../../services/registration-controller/determinant-controller.service";
import {MyFormioComponent} from "../../my-formio/my-formio.component";
import Helpers from "../../../utils/helpers";
import {FormControllerService} from "../../../services/form-controller/form-controller.service";

@Component({
  selector: "print-document-detail",
  templateUrl: "./print-document-detail.component.html",
  providers: [PrintDocumentControllerService, FormioControllerService, TagControllerService],
})

export class PrintDocumentDetailComponent extends TranslatedBaseError implements OnInit, OnDestroy {

  public serviceId: string;
  public focusCompKey: string;
  public documentId: string;
  public isLoading: boolean = false;
  public document: PrintDocument = new PrintDocument();
  public determinantMapById: { [key: string]: string } = {};
  public tags: any[] = [];
  public selectedTag: Tag[];
  public urlMoustach: string;
  public activeTab: string = "certificate-form";
  @ViewChild('myFormio', { static: false }) myFormio: MyFormioComponent;

  public isSchemaLoaded = false;
  public showFormio: boolean = true;
  public hidePreview: boolean = false;
  public showHistory: boolean = false;
  public showMicroPublishModal: boolean = false;

  private pagePropertiesSave$ = new Subject<void>();
  private pagePropertiesSaveSub: Subscription;

  constructor(private route: ActivatedRoute,
              public printDocController: PrintDocumentControllerService,
              public determinantService: DeterminantControllerService,
              private tagController: TagControllerService,
              public store: Store,
              private formControllerService: FormControllerService,
              toastr: ToastrService, public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    if (environment.HIDE_PREVIEW) {
      this.hidePreview = true;
    }
    this.pagePropertiesSaveSub = this.pagePropertiesSave$.pipe(
      debounceTime(500),
      switchMap(() => this.printDocController.saveDocument(this.document)),
    ).subscribe(
      () => { /* silent autosave */ },
      (error) => this.showErrorMessage(error),
    );
    this.route.params.subscribe((params) => {
      this.serviceId = params.serviceId;
      this.getTags();
      this.documentId = params.documentId;
      this.showFormio = false;

      const part = this.documentId.split("#");
      if (part.length > 1) {
        this.documentId = part[0];
        this.focusCompKey = part[1];
      }
      if (this.documentId) { this.loadDocument(); } else {
        if (params.templateId) {
          this.loadTemplateSchema(params.templateId);
        }
      }
      this.getServiceDeterminantKeyName();
    });
  }

  public ngOnDestroy() {
    if (this.pagePropertiesSaveSub) {
      this.pagePropertiesSaveSub.unsubscribe();
    }
  }

  public onPagePropertiesChange(props: any) {
    if (!this.document || !this.document.id) {
      return;
    }
    this.document.pageProperties = props;
    this.pagePropertiesSave$.next();
  }

  public getServiceDeterminantKeyName() {
    this.determinantService.getServiceDeterminantIdAndName(this.serviceId).subscribe(
      (data) => Object.assign(this.determinantMapById, data),
    );
  }

  public openHistory() {
    this.showHistory = true;
  }

  public closeHistory() {
    this.showHistory = false;
  }

  public openMicroPublishModal() {
    this.showMicroPublishModal = true;
  }

  public closeMicroPublishModal() {
    this.showMicroPublishModal = false;
  }

  public changeForm(revisionNumber: string): void {
    if (revisionNumber) {
      // Step 1: GET the schema for validation
      this.printDocController.getFormSchemaForRevision(this.document.id, revisionNumber).subscribe((schema) => {
        if (schema) {
          // Step 2: Validate schema for duplicate keys
          const result = Helpers.validateFormSchema(schema);
          if (Array.isArray(result) && result.length > 0) {
            this.error("Please reload page!\n" + Helpers.generateValidationErrorMessages(result).join("\n"), 'Validation error');
          }
          else {
            // Step 3: If valid, call PUT to revert (triggers cleanup of orphaned relations)
            this.printDocController.revertFormToRevision(this.document.id, revisionNumber).subscribe(
              () => {
                // Step 4: Reload the document after successful reversion
                this.printDocController.getPrintDocument(this.document.id).subscribe(
                  (data) => {
                    this.document = data ? Object.assign(new PrintDocument(), data) : this.document;
                    this.responseSuccessful("Form reverted successfully");
                  },
                  (error) => this.showErrorMessage(error)
                );
              },
              (error) => this.showErrorMessage(error)
            );
          }
        }
      });
    }
  }

  public getTags() {
    this.tagController.getServiceTagList(this.serviceId).subscribe((tg) => {
      if (tg) {
        this.tags = _.uniqWith(tg, _.isEqual);
      }
    });
  }

  public updateUrlWithTag() {
    // console.log(this.selectedTag);
    if (this.document && this.document.isExternal && this.document.externalUrl) {
      if (this.selectedTag) {
        this.document.externalUrl = this.document.externalUrl.slice(0, this.document.externalUrl.indexOf("{"));
      }
      this.document.externalUrl = this.document.externalUrl + "{{" + this.selectedTag + "}}";
      this.saveDocument();
    }
  }

  public loadTemplateSchema(templateId: string) {
    this.printDocController.getTemplateSchema(templateId, this.serviceId).subscribe(
      (data) => {
        this.isSchemaLoaded = true;
        this.document.formSchema = data;
        this.showFormio = true;
      },
    );
  }

  public copyUrl() {
    this.urlMoustach = this.tags?.find((m) => m.name === `License URL ${this.document.name}` || m.name === `License external URL ${this.document.name}`)[0]?.key || this.urlMoustach;
    navigator.clipboard.writeText(this.urlMoustach).then((e) => {
      this.responseSuccessful("URL Copied!" + " => " + this.urlMoustach);
    }, (e) => {
      this.showErrorMessage("URL not Copied!");
    });
  }

  private loadDocument() {
    this.printDocController.getPrintDocument(this.documentId).subscribe(
      (data) => {
        this.isSchemaLoaded = true;
        this.document = (data ? Object.assign(new PrintDocument(), data) : this.document);
        this.document.tutorials = Object.assign(new TutorialBase().tutorials, this.document.tutorials);

        if (this.document.isExternal) {
          this.urlMoustach = "{{data.license-external-url_" + this.document.id + "-" + this.formatTag(this.document.name) + "}}";
        } else {
          this.urlMoustach = "{{data.license-url_" + this.document.id + "-" + this.formatTag(this.document.name) + "}}";
        }
        this.showFormio = true;
        this.fillSelectedTag();
      },
    );
  }

  private formatTag(name) {
    const split = name.split(" ");
    let tag = "";
    for ( let i = 0; i < split.length; i++) {
      const part = split[i].toLowerCase();
      tag += i === 0 ? part : split[i].charAt(0).toUpperCase() + part.slice(1);
    }
    return _.deburr(tag).replaceAll("-", "");
  }

  public fillSelectedTag() {
    if (this.document && this.document.isExternal) {
      this.selectedTag = this.getTagFromString(this.document.externalUrl);
    }
  }

  public getTagFromString(tagString) {
    const tagStart = tagString.indexOf("{{");
    const tagEnd = tagString.indexOf("}}");
    if (tagStart !== -1 && tagEnd !== -1) {
      const tag = tagString.slice(tagStart + 2, tagEnd);
      return tag;
    }
    else {
      return;
    }
  }

  public toggleOperational() {
    this.isLoading = true;
    this.document.active = !this.document.active;
    this.printDocController.saveDocument(this.document).subscribe(
      (data) => {
        this.isLoading = false;
        if (this.document.active) {
          this.responseSuccessful("Document activated!");
        } else {
          this.responseSuccessful("Document inactivated!");
        }
      },
      (error) => {
        this.loadDocument();
        return this.showErrorMessage(error);
      },
    );
  }

  public toggleExternal() {
    this.isLoading = true;
    this.document.isExternal = !this.document.isExternal;
    this.printDocController.saveDocument(this.document).subscribe(
      (data) => {
        this.isLoading = false;
        if (this.document.isExternal) {
          this.responseSuccessful("Document is remote!");
          this.urlMoustach = "{{license-external-url_" + this.document.id + "}}";
        } else {
          this.responseSuccessful("Document is internal!");
        }
      },
      (error) => {
        this.loadDocument();
        return this.showErrorMessage(error);
      },
    );
  }

  public saveDocument() {
    if (this.document.name && this.document.name.length >= 2 && this.document.shortName && this.document.shortName.length >= 2) {
      this.document.name = this.document.name.trim().replace(/[^a-zA-Z0-9 _-]/g, "");
      this.document.shortName = this.document.shortName.trim().replace(/[^a-zA-Z0-9 _-]/g, "");
      this.printDocController.saveDocument(this.document).subscribe((data) => {
        this.getTags();
        // eslint-disable-line
        this.responseSuccessful("Document is saved!");
      },
        (error) => {
          return this.showErrorMessage(error);
        });
    } else {
      this.error("Please verify certificate name and shortname.", "Save failed");
    }
  }

  public showIconFor() {
    if (this.isLoading) {
      return "fa-spinner fa-spin";
    }
    if (this.document.active) {
      return "fa-toggle-large-on";
    }
    return "fa-toggle-large-on fa-flip-horizontal";
  }

  public showIconForExternal() {
    if (this.isLoading) {
      return "fa-spinner fa-spin";
    }
    if (this.document.isExternal) {
      return "fa-toggle-large-on";
    }
    return "fa-toggle-large-on fa-flip-horizontal";
  }

  public saveTutorial(e) {
    if (e) {
      this.document.tutorials.general = e;
      this.saveForm(this.document.formSchema);
    }
  }

  public saveForm(formSchema: any) {
    if (this.document.id) {
      // add version to distinguish old and new form builder components
      if (formSchema && formSchema.components) {
        for (const comp of formSchema.components) {
          FormUtils.updateWithVersion(comp);
          FormUtils.updateTableAttributes(comp);
        }
      }
    }
    this.document.formSchema = formSchema;
    this.saveDocument();
  }

  public showPreview() {
    this.printDocController.getPreviewUrl(this.document.id).subscribe((url) => {
      window.open(`${url}?ignoreSizeLimit=1`, "_blank");
    }, this.showErrorMessage.bind(this));
  }

  public notifyTabChange(tab) {
    localStorage.setItem("certificate-details-tab", tab);
    this.activeTab = tab;
  }

  public downloadSchema() {
    this.formControllerService.downloadSchemaWithRelations(this.document.id).subscribe(
      (data) => {
        const blob = new Blob([data], { type: "application/octet-stream" });
        const downloadURL = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = downloadURL;
        link.download = `${this.store.state.common.serviceName}_print_document_schema.json`;
        link.click();
      },
      (error) => this.showErrorMessage(error),
    );
  }
}
